import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import {
  Users,
  User,
  Bell,
  X,
  Moon,
  Sun,
  LogOut,
  Star,
  Grid,
  Settings,
  Globe
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../context/UserContext';

const getMobileActiveColor = (isDarkMode) => (isDarkMode ? '#EFF3F7' : '#131415');

const Overlay = styled.div`
  display: ${({ $isOpen }) => $isOpen ? 'flex' : 'none'};
  position: fixed;
  top: var(--polka-safe-top, 0px);
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  justify-content: flex-end;
  align-items: flex-end;
`;

const MenuContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 400px;
  padding: 20px;
  padding-bottom: max(20px, env(safe-area-inset-bottom), var(--polka-safe-bottom, 0px));
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease;
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const MenuTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const MenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.2s ease;
  margin-bottom: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  &.active {
    background: transparent;
    color: ${({ theme }) => getMobileActiveColor(theme.isDarkMode)};
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  font-weight: 500;
  background: none;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;
  margin-bottom: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 12px 0;
`;

const FandomIcon = styled.img`
  width: 22px;
  height: 22px;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
    : 'brightness(0) saturate(100%) invert(9%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'};
  transition: filter 0.3s ease;
`;

const BurgerMenu = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { logout } = useUser();

  const handleItemClick = () => {
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <MenuContainer $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <MenuHeader>
          <MenuTitle>{t('nav.menu')}</MenuTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </MenuHeader>

        <MenuItem to="/friends" onClick={handleItemClick}>
          <Users />
          <span>{t('nav.friends')}</span>
        </MenuItem>

        <MenuItem to="/wishlist" onClick={handleItemClick}>
          <Star />
          <span>{t('nav.wishlist')}</span>
        </MenuItem>

        <MenuItem to="/collections" onClick={handleItemClick}>
          <Grid />
          <span>{t('nav.collections')}</span>
        </MenuItem>

        <MenuItem to="/fandoms" onClick={handleItemClick}>
          <FandomIcon src="/fandom.svg" alt={t('nav.fandoms')} />
          <span>{t('nav.fandoms')}</span>
        </MenuItem>

        <MenuItem to="/profile" onClick={handleItemClick}>
          <User />
          <span>{t('nav.profile')}</span>
        </MenuItem>

        <MenuItem to="/notifications" onClick={handleItemClick}>
          <Bell />
          <span>{t('nav.notifications')}</span>
        </MenuItem>

        <Divider />

        <MenuButton onClick={toggleTheme}>
          {isDarkMode ? <Sun /> : <Moon />}
          <span>{isDarkMode ? t('nav.lightTheme') : t('nav.darkTheme')}</span>
        </MenuButton>

        <MenuItem to="/settings" onClick={handleItemClick}>
          <Settings />
          <span>{t('nav.settings')}</span>
        </MenuItem>

        <MenuButton onClick={handleLogout}>
          <LogOut />
          <span>{t('nav.logout')}</span>
        </MenuButton>
      </MenuContainer>
    </Overlay>
  );
};

export default BurgerMenu;
